import React, { useState, useRef, useEffect, useCallback, memo } from 'react';

const InteractiveLogo = memo(({ className = '', onClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState([]);
  const logoRef = useRef(null);
  const animationRef = useRef(null);

  const createParticles = useCallback((x, y) => {
    const newParticles = Array.from({ length: 8 }, (_, i) => ({
      id: Date.now() + i,
      x,
      y,
      angle: (i / 8) * Math.PI * 2,
      speed: 2 + Math.random() * 3,
      size: 3 + Math.random() * 4,
      life: 1,
    }));
    setParticles((prev) => [...prev, ...newParticles]);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (logoRef.current) {
      const rect = logoRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 30;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 30;
      setMousePosition({ x, y });
    }
  }, []);

  const handleTouch = useCallback((e) => {
    e.preventDefault();
    if (logoRef.current) {
      const touch = e.touches[0];
      const rect = logoRef.current.getBoundingClientRect();
      const x = ((touch.clientX - rect.left) / rect.width - 0.5) * 30;
      const y = ((touch.clientY - rect.top) / rect.height - 0.5) * 30;
      setMousePosition({ x, y });
      
      // Create particles on touch
      createParticles(touch.clientX - rect.left, touch.clientY - rect.top);
    }
  }, [createParticles]);

  useEffect(() => {
    const animate = () => {
      setParticles((prev) =>
        prev
          .map((p) => ({
            ...p,
            x: p.x + Math.cos(p.angle) * p.speed,
            y: p.y + Math.sin(p.angle) * p.speed,
            life: p.life - 0.03,
          }))
          .filter((p) => p.life > 0)
      );
      animationRef.current = requestAnimationFrame(animate);
    };
    
    if (particles.length > 0) {
      animationRef.current = requestAnimationFrame(animate);
    }
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [particles.length]);

  const handleClick = useCallback(() => {
    if (logoRef.current) {
      const rect = logoRef.current.getBoundingClientRect();
      createParticles(rect.width / 2, rect.height / 2);
    }
    // Scroll to top
    if (onClick) {
      onClick();
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [createParticles, onClick]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
    setMousePosition({ x: 0, y: 0 });
  }, []);

  return (
    <div
      ref={logoRef}
      className={`relative cursor-pointer select-none ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouch}
      onTouchMove={handleTouch}
      onClick={handleClick}
      style={{
        perspective: '1000px',
      }}
    >
      {/* Glow effect behind logo */}
      <div
        className="absolute inset-0 transition-all duration-500 will-change-transform"
        style={{
          background: `radial-gradient(circle, rgba(212, 166, 52, ${isHovered ? 0.6 : 0.3}) 0%, transparent 70%)`,
          transform: `scale(${isHovered ? 1.6 : 1.4})`,
          filter: `blur(${isHovered ? 35 : 25}px)`,
        }}
      />
      
      {/* Logo container with 3D transform */}
      <div 
        className="relative z-10 w-full h-full will-change-transform"
        style={{
          transform: `rotateX(${-mousePosition.y}deg) rotateY(${mousePosition.x}deg) scale(${isHovered ? 1.05 : 1})`,
          transition: 'transform 0.3s ease',
        }}
      >
        {/* Main logo - transparent PNG */}
        <img
          src="/assets/logo.png"
          alt="Shachi Collections Logo"
          className="w-full h-full object-contain"
          style={{
            filter: isHovered 
              ? 'drop-shadow(0 0 20px rgba(212, 166, 52, 0.8)) brightness(1.1)' 
              : 'drop-shadow(0 0 10px rgba(212, 166, 52, 0.5))',
          }}
          draggable="false"
          loading="lazy"
        />
      </div>
      
      {/* Particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full pointer-events-none z-30"
          style={{
            left: particle.x,
            top: particle.y,
            width: particle.size,
            height: particle.size,
            background: `radial-gradient(circle, rgba(244, 196, 48, ${particle.life}) 0%, rgba(212, 166, 52, ${particle.life * 0.5}) 100%)`,
            transform: 'translate(-50%, -50%)',
            boxShadow: `0 0 ${particle.size * 2}px rgba(212, 166, 52, ${particle.life})`,
          }}
        />
      ))}
      
      {/* Shimmer overlay on hover */}
      {isHovered && (
        <div
          className="absolute inset-0 pointer-events-none z-20 overflow-hidden"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(244, 196, 48, 0.15) 50%, transparent 100%)',
            animation: 'shimmer 2s infinite',
          }}
        />
      )}
    </div>
  );
});

InteractiveLogo.displayName = 'InteractiveLogo';

export default InteractiveLogo;
